import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { commonStrings, lineStrings } from 'constants/strings';
import { ERichMenuTemplateCategory, type TRichMenuTemplate } from 'types';

import { RichMenuTemplateThumbnail } from './RichMenuTemplateThumbnail';

type RichMenuTemplateModalProps = {
  open: boolean;
  templates: TRichMenuTemplate[];
  selectedId: string;
  onClose: () => void;
  onApply: (templateId: string) => void;
};

const strings = lineStrings.management.richMenu.create;

export function RichMenuTemplateModal({
  open,
  templates,
  selectedId,
  onClose,
  onApply,
}: RichMenuTemplateModalProps) {
  const [pendingId, setPendingId] = useState(selectedId);

  useEffect(() => {
    if (open) {
      setPendingId(selectedId);
    }
  }, [open, selectedId]);

  const largeTemplates = useMemo(
    () =>
      templates.filter((t) => t.category === ERichMenuTemplateCategory.Large),
    [templates],
  );

  const compactTemplates = useMemo(
    () =>
      templates.filter((t) => t.category === ERichMenuTemplateCategory.Compact),
    [templates],
  );

  const handleApply = useCallback(() => {
    onApply(pendingId);
  }, [pendingId, onApply]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal open={open} onClose={handleCancel} size="xl">
      <div className="flex max-h-[min(85vh,800px)] flex-col gap-0">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-200 pb-4">
          <h2 className="text-base font-bold text-neutral-900">
            {strings.templateModalTitle}
          </h2>
          <button
            type="button"
            className="rounded-lg p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label={commonStrings.close}
            onClick={handleCancel}
          >
            <Icon icon="close" width={20} height={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-4 pr-1">
          <section className="mb-8">
            <h3 className="text-sm font-semibold text-neutral-900">
              {strings.templateModalLargeTitle}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-neutral-600">
              {strings.templateModalLargeDescription}
            </p>
            <div className="mt-4 flex flex-wrap gap-4 pl-1">
              {largeTemplates.map((template) => (
                <RichMenuTemplateThumbnail
                  key={template.id}
                  template={template}
                  isSelected={pendingId === template.id}
                  labelJa={template.label}
                  onClick={() => setPendingId(template.id)}
                />
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-neutral-900">
              {strings.templateModalCompactTitle}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-neutral-600">
              {strings.templateModalCompactDescription}
            </p>
            <div className="mt-4 flex flex-wrap gap-4 pl-1">
              {compactTemplates.map((template) => (
                <RichMenuTemplateThumbnail
                  key={template.id}
                  template={template}
                  isSelected={pendingId === template.id}
                  labelJa={template.label}
                  onClick={() => setPendingId(template.id)}
                />
              ))}
            </div>
          </section>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-neutral-200 pt-4">
          <Button type="button" variant="outline" onClick={handleCancel}>
            {strings.templateModalCancel}
          </Button>
          <Button type="button" variant="primary" onClick={handleApply}>
            {strings.templateModalApply}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
